import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import SvgLoadingAnimation from "../../../components/loading";
import Avatar from "../../../components/avatar/Avatar";
import Button from "../../../components/button/Button";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import Textarea from "../../../components/form-item/Textarea";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../components/date-display/DateDisplay";
import {
  useDeleteNoteChangeMutation,
  useGetNoteChangeQuery,
  useMergeNoteChangeMutation,
  useReviewNoteChangeMutation,
  useUpdateNoteChangeMutation,
  type NoteChangeConflictData,
  type NoteChangeDetail,
  type NoteChangePayload,
  type NoteChangeReviewAction,
  type NoteSection,
} from "../../../app/services/notes";
import { CHANGE_STATUS_LABELS } from "./ChangesListView";
import ChangeDiff from "./ChangeDiff";
import ChangeEditModal from "./ChangeEditModal";
import ConflictResolveModal from "./ConflictResolveModal";
import SubmitChangeModal from "./SubmitChangeModal";

/**
 * 変更提案（プルリクエスト）の詳細。
 *
 * - 差分（現在の内容 → 提案内容）とレビュー履歴・承認状態を表示する
 * - メンテナー/オーナー: 承認 / 承認してマージ / 差し戻し / 却下 /
 *   オーナーにレビュー依頼
 * - 起票者: 修正（下書き/レビュー待ちの内容更新）/ プルリク送信 / 取り下げ
 * - マージ時にコンフリクト（409）が返ったら解消モーダルを開き、
 *   最終調整した内容で再実行する
 */

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  /* NoteView と同じく本文幅を 720px にし、左右の余白は画面が狭いときだけ取る */
  width: min(720px, 100% - 32px);
  margin: 0 auto;
  padding: 24px 0 96px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  border: none;
  background: transparent;
  color: var(--soremo-light-gray);
  cursor: pointer;
  padding: 0;

  .material-symbols-rounded {
    font-size: 18px;
  }

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
    }
  }
`;

const MetaRow = styled.div.attrs({
  className: "u-row u-gap8 u-items-center bodytext-11",
})`
  flex-wrap: wrap;
  color: var(--soremo-light-gray);
`;

const SectionBox = styled.div`
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  padding: 12px 16px;
  background: #fcfcfc;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
`;

const NoticeBox = styled.div<{ $tone: "warn" | "info" }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === "warn" ? "rgba(232, 152, 41, 0.4)" : "rgba(0, 154, 206, 0.3)"};
  background: ${({ $tone }) =>
    $tone === "warn" ? "rgba(232, 152, 41, 0.08)" : "rgba(0, 154, 206, 0.06)"};

  .material-symbols-rounded {
    font-size: 18px;
    color: ${({ $tone }) =>
      $tone === "warn" ? "#e89829" : "var(--soremo-blue)"};
    flex-shrink: 0;
  }
`;

const ReviewRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--soremo-border);

  &:last-child {
    border-bottom: none;
  }

  .review-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .review-comment {
    white-space: pre-wrap;
    color: var(--soremo-gray);
  }
`;

const REVIEW_ACTION_LABELS: Record<NoteChangeReviewAction, string> = {
  approve: "承認しました",
  request_changes: "差し戻しました",
  reject: "却下しました",
  request_owner_review: "オーナーにレビューを依頼しました",
};

export interface ChangeDetailViewProps {
  changeId: string;
  /** ツリーのセクション一覧（自分のロール解決と「一覧へ戻る」先に使用） */
  sections: NoteSection[];
  /** 一覧へ戻る（セクションが分かる場合はその PR 一覧へ） */
  onBack: (sectionId?: string) => void;
  /** マージ後・対象表示リンクの遷移 */
  onOpenTarget: (targetType: "note" | "card", targetId: string) => void;
}

const ChangeDetailView: React.FC<ChangeDetailViewProps> = ({
  changeId,
  sections,
  onBack,
  onOpenTarget,
}) => {
  const { data, isLoading, error } = useGetNoteChangeQuery({
    change_id: changeId,
  });
  const [reviewChange, { isLoading: reviewing }] =
    useReviewNoteChangeMutation();
  const [mergeChange, { isLoading: merging }] = useMergeNoteChangeMutation();
  const [updateChange, { isLoading: updatingChange }] =
    useUpdateNoteChangeMutation();
  const [deleteChange, { isLoading: withdrawing }] =
    useDeleteNoteChangeMutation();

  const [comment, setComment] = useState("");
  const [resendOnMerge, setResendOnMerge] = useState(false);
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<
    NoteChangeConflictData["data"] | null
  >(null);

  const change: NoteChangeDetail | null = data?.data?.change ?? null;

  /** 対象セクションでの自分のロール（承認完了予測に使用） */
  const myRole = useMemo(
    () => sections.find((s) => s.id === change?.section_id)?.my_role,
    [sections, change],
  );

  /** 自分が承認したらマージ条件が満たされるか（BE で最終判定される予測値） */
  const wouldCompleteWithMyApproval = useMemo(() => {
    if (!change) return false;
    const a = change.approval;
    const ownerOk =
      !a.owner_approval_required || a.owner_approved || myRole === "owner";
    return a.approvals + 1 >= a.required_approvals && ownerOk;
  }, [change, myRole]);

  const handleReview = useCallback(
    async (action: NoteChangeReviewAction) => {
      if (!change) return;
      if (action === "request_changes" && !comment.trim()) {
        toast.error("差し戻しにはコメントを入力してください");
        return;
      }
      try {
        await reviewChange({
          change_id: change.id,
          action,
          comment: comment.trim(),
        }).unwrap();
        setComment("");
        setConfirmingReject(false);
        toast.success(REVIEW_ACTION_LABELS[action]);
      } catch {
        toast.error("操作に失敗しました");
      }
    },
    [change, comment, reviewChange],
  );

  const handleMerge = useCallback(
    async (resolvedPayload?: NoteChangePayload) => {
      if (!change) return;
      try {
        await mergeChange({
          change_id: change.id,
          ...(resolvedPayload ? { resolved_payload: resolvedPayload } : {}),
          ...(change.target_type === "card" && resendOnMerge
            ? { reset_reactions: true }
            : {}),
        }).unwrap();
        setConflict(null);
        setComment("");
        toast.success("マージしました");
      } catch (err) {
        const e = err as {
          status?: number;
          data?: NoteChangeConflictData & { message?: string };
        };
        if (e?.status === 409 && e.data?.data) {
          setConflict(e.data.data);
          return;
        }
        if (e?.status === 400) {
          // 承認は記録済みだが条件未充足（他レビュアーの承認待ち）
          toast("承認を記録しました。マージには追加の承認が必要です");
          return;
        }
        toast.error("マージに失敗しました");
      }
    },
    [change, mergeChange, resendOnMerge],
  );

  const handleWithdraw = useCallback(async () => {
    if (!change) return;
    try {
      await deleteChange({ change_id: change.id }).unwrap();
      toast.success("取り下げました");
      setConfirmingWithdraw(false);
      onBack();
    } catch {
      toast.error("取り下げに失敗しました");
    }
  }, [change, deleteChange, onBack]);

  const handleSubmit = useCallback(
    async (description: string) => {
      if (!change) return;
      try {
        await updateChange({
          change_id: change.id,
          description,
          submit: true,
        }).unwrap();
        setSubmitting(false);
        toast.success("プルリクエストを送信しました");
      } catch {
        toast.error("送信に失敗しました");
      }
    },
    [change, updateChange],
  );

  if (isLoading) return <SvgLoadingAnimation />;

  if (error || !change) {
    return (
      <ViewWrapper>
        <BackButton onClick={() => onBack()}>
          <span className="material-symbols-rounded">arrow_back</span>
          <span className="bodytext-13">一覧へ戻る</span>
        </BackButton>
        <p className="bodytext-13 u-text-light-gray">
          変更提案が見つからないか、閲覧権限がありません
        </p>
      </ViewWrapper>
    );
  }

  const isOpen = change.status === "open";
  const isDraft = change.status === "draft";
  const isAuthorActionable = change.permissions.can_update;
  const statusMeta = CHANGE_STATUS_LABELS[change.status];
  const approval = change.approval;

  const diffBefore =
    change.kind === "update" ? (change.current ?? change.base) : null;

  // 移動の提案（承認不要フォルダの外へ出す移動など）の移動元・移動先
  const changeSection = sections.find((s) => s.id === change.section_id);
  const folderTitle = (folderId: string | null | undefined) =>
    folderId
      ? (changeSection?.folders.find((f) => f.id === folderId)?.title ??
        "（削除されたフォルダ）")
      : "セクション直下";
  const move = change.kind === "update" ? change.payload.move : undefined;
  const moveFrom = changeSection?.notes.find(
    (n) => n.id === change.target_id,
  )?.folder_id;

  return (
    <ViewWrapper>
      <BackButton onClick={() => onBack(change.section_id)}>
        <span className="material-symbols-rounded">arrow_back</span>
        <span className="bodytext-13">一覧へ戻る</span>
      </BackButton>

      <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
        <div className="u-row u-gap8 u-items-center">
          <h2
            className="heading-spacing-18 u-line-height-150"
            style={{ flex: 1, minWidth: 0 }}
          >
            {change.title}
          </h2>
          <StatusBadge label={statusMeta.label} leftIcon={statusMeta.icon} />
        </div>
        <MetaRow>
          <Avatar src={change.author?.avatar ?? undefined} size={32} />
          <span>{change.author?.display_name}</span>
          <DateDisplay
            date={change.opened_at ?? change.created ?? ""}
            type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
          />
          <span>
            {change.target_type === "card" ? "カード" : "ノート"}
            {change.kind === "create" ? "の新規作成" : "の更新"}
          </span>
          {change.target_id && (
            <Button
              size="small"
              theme="secondary"
              variant="text"
              width="fit"
              onClick={() =>
                onOpenTarget(change.target_type, change.target_id as string)
              }
            >
              対象を表示
            </Button>
          )}
        </MetaRow>
        {change.description && (
          <p className="bodytext-13 u-line-height-150 u-text-gray">
            {change.description}
          </p>
        )}
      </div>

      {isOpen && change.has_conflict && (
        <NoticeBox $tone="warn">
          <span className="material-symbols-rounded">warning</span>
          <span className="bodytext-11 u-line-height-150">
            この提案の作成後に対象が更新されています。マージ時にコンフリクトの
            解消（最終内容の確認・調整）が必要です。
          </span>
        </NoticeBox>
      )}

      {move && (
        <NoticeBox $tone="info">
          <span className="material-symbols-rounded">drive_file_move</span>
          <span className="bodytext-11 u-line-height-150">
            {change.status === "merged"
              ? `「${folderTitle(move.folder_id)}」へ移動しました。`
              : `移動の提案: 「${folderTitle(moveFrom)}」から「${folderTitle(move.folder_id)}」へ移動します。承認されるとこの内容で移動先に表示されます。`}
          </span>
        </NoticeBox>
      )}

      <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
        <h3 className="heading-13 u-line-height-150">変更内容</h3>
        <ChangeDiff
          before={diffBefore}
          after={change.payload}
          isCard={change.target_type === "card"}
        />
      </div>

      {(isOpen || change.status === "merged") && (
        <SectionBox>
          <h3 className="heading-13 u-line-height-150">承認状態</h3>
          <div className="u-row u-gap8 u-items-center bodytext-13">
            <span
              className="material-symbols-rounded"
              style={{
                fontSize: "18px",
                color:
                  approval.approvals >= approval.required_approvals
                    ? "var(--soremo-blue)"
                    : "var(--soremo-light-gray)",
              }}
            >
              how_to_reg
            </span>
            <span>
              承認 {approval.approvals} / {approval.required_approvals}
            </span>
            {approval.approved_by.length > 0 && (
              <span className="bodytext-11 u-text-light-gray">
                （{approval.approved_by.map((u) => u.display_name).join("、")}）
              </span>
            )}
          </div>
          {approval.owner_approval_required && (
            <div className="u-row u-gap8 u-items-center bodytext-13">
              <span
                className="material-symbols-rounded"
                style={{
                  fontSize: "18px",
                  color: approval.owner_approved
                    ? "var(--soremo-blue)"
                    : "var(--soremo-light-gray)",
                }}
              >
                verified_user
              </span>
              <span>
                オーナー承認{approval.owner_approved ? "済み" : "が必要"}
              </span>
            </div>
          )}
          {change.status === "merged" && (
            <p className="bodytext-11 u-text-light-gray">
              {change.merged_by?.display_name} さんがマージしました
              {change.merged_at && (
                <>
                  {" "}
                  <DateDisplay
                    date={change.merged_at}
                    type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
                  />
                </>
              )}
            </p>
          )}
        </SectionBox>
      )}

      {change.reviews.length > 0 && (
        <div className="u-col u-gap4" style={{ alignItems: "stretch" }}>
          <h3 className="heading-13 u-line-height-150">レビュー履歴</h3>
          <SectionBox>
            {change.reviews.map((review) => (
              <ReviewRow key={review.id}>
                <Avatar src={review.reviewer?.avatar ?? undefined} size={32} />
                <span className="review-body">
                  <span className="bodytext-11 u-text-light-gray">
                    {review.reviewer?.display_name} さんが
                    {REVIEW_ACTION_LABELS[review.action]}
                    {review.created && (
                      <>
                        {" "}
                        <DateDisplay
                          date={review.created}
                          type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
                        />
                      </>
                    )}
                  </span>
                  {review.comment && (
                    <span className="review-comment bodytext-13 u-line-height-150">
                      {review.comment}
                    </span>
                  )}
                </span>
              </ReviewRow>
            ))}
          </SectionBox>
        </div>
      )}

      {/* レビューアクション（メンテナー / オーナー） */}
      {isOpen && (change.permissions.can_review || change.permissions.can_merge) && (
        <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
          <h3 className="heading-13 u-line-height-150">レビュー</h3>
          <Textarea
            value={comment}
            rows={3}
            maxLength={2000}
            placeholder="コメント（差し戻し時は必須）"
            onChange={(e) => setComment(e.target.value)}
          />
          {change.target_type === "card" && (
            <label
              className="u-row u-gap8 u-items-center bodytext-13"
              style={{ cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={resendOnMerge}
                onChange={() => setResendOnMerge((v) => !v)}
                style={{
                  width: 18,
                  height: 18,
                  accentColor: "var(--soremo-blue)",
                }}
              />
              マージ時に既読をリセットしてやることリストへ再送する
            </label>
          )}
          <div
            className="u-row u-gap8 u-items-center"
            style={{ flexWrap: "wrap", justifyContent: "flex-end" }}
          >
            {change.permissions.can_review && (
              <>
                <Button
                  size="small"
                  theme="secondary"
                  variant="text"
                  width="fit"
                  onClick={() => setConfirmingReject(true)}
                  disabled={reviewing || merging}
                >
                  却下
                </Button>
                <Button
                  size="small"
                  theme="secondary"
                  variant="outlined"
                  width="fit"
                  onClick={() => void handleReview("request_changes")}
                  disabled={reviewing || merging}
                >
                  差し戻し
                </Button>
                {approval.owner_approval_required &&
                  !approval.owner_approved && (
                    <Button
                      size="small"
                      theme="secondary"
                      variant="outlined"
                      width="fit"
                      onClick={() => void handleReview("request_owner_review")}
                      disabled={reviewing || merging}
                    >
                      オーナーにレビュー依頼
                    </Button>
                  )}
              </>
            )}
            {approval.mergeable && change.permissions.can_merge ? (
              <Button
                size="small"
                theme="primary"
                width="fit"
                onClick={() => void handleMerge()}
                disabled={merging}
              >
                {merging ? "マージ中..." : "マージ"}
              </Button>
            ) : change.permissions.can_review ? (
              wouldCompleteWithMyApproval ? (
                <Button
                  size="small"
                  theme="primary"
                  width="fit"
                  onClick={() => void handleMerge()}
                  disabled={merging || reviewing}
                >
                  {merging ? "マージ中..." : "承認してマージ"}
                </Button>
              ) : (
                <Button
                  size="small"
                  theme="primary"
                  width="fit"
                  onClick={() => void handleReview("approve")}
                  disabled={reviewing || merging}
                >
                  承認
                </Button>
              )
            ) : null}
          </div>
        </div>
      )}

      {/* 起票者アクション */}
      {isAuthorActionable && (
        <div
          className="u-row u-gap8 u-items-center"
          style={{ justifyContent: "flex-end", flexWrap: "wrap" }}
        >
          <Button
            size="small"
            theme="secondary"
            variant="text"
            width="fit"
            onClick={() => setConfirmingWithdraw(true)}
            disabled={withdrawing}
          >
            取り下げ
          </Button>
          <Button
            size="small"
            theme="secondary"
            variant="outlined"
            width="fit"
            onClick={() => setEditing(true)}
          >
            内容を修正
          </Button>
          {isDraft && (
            <Button
              size="small"
              theme="primary"
              width="fit"
              onClick={() => setSubmitting(true)}
              disabled={updatingChange}
            >
              プルリクエストを送信
            </Button>
          )}
        </div>
      )}

      {confirmingReject && (
        <ConfirmModal
          message="このプルリクエストを却下しますか？却下すると再開できません。"
          confirmLabel="却下"
          loading={reviewing}
          onConfirm={() => void handleReview("reject")}
          onCancel={() => setConfirmingReject(false)}
        />
      )}

      {confirmingWithdraw && (
        <ConfirmModal
          message="この変更提案を取り下げますか？内容は破棄されます。"
          confirmLabel="取り下げ"
          loading={withdrawing}
          onConfirm={() => void handleWithdraw()}
          onCancel={() => setConfirmingWithdraw(false)}
        />
      )}

      {editing && (
        <ChangeEditModal change={change} onClose={() => setEditing(false)} />
      )}

      {submitting && (
        <SubmitChangeModal
          title={change.title}
          loading={updatingChange}
          initialDescription={change.description}
          onSubmit={(description) => void handleSubmit(description)}
          onClose={() => setSubmitting(false)}
        />
      )}

      {conflict && (
        <ConflictResolveModal
          targetType={change.target_type}
          conflict={conflict}
          merging={merging}
          onResolve={(payload) => void handleMerge(payload)}
          onClose={() => setConflict(null)}
        />
      )}
    </ViewWrapper>
  );
};

export default ChangeDetailView;
